import locations from '../locations.json';
import { Flex, Text, Card, Grid, Dialog } from '@radix-ui/themes';
import React from 'react';

interface LogEvent {
  id: string;
  ip: string;
  event: UserInfoLog[] | any[];
  time: string;
}

interface ErrorEvent extends LogEvent {
  error: true;
  event: ErrorInfoLog;
}

export type Event = LogEvent | ErrorEvent;

type ErrorInfoLog = [string, { stack: string, name: string, message: string }];

type UserInfoLog = [
  'USER_INFO', { browser: string, language: string }
];

interface LogViewerProps {
  logs: { events: Event[] };
}

const DAY_IN_MS = 24 * 60 * 60 * 1000;

function getDaysAgo(time: string) {
  const midnight = Date.parse(new Date().toLocaleDateString());
  const today = new Date().getTime() - midnight;
  const diff = new Date().getTime() - Date.parse(time);
  const daysAgo = diff < today ? null : Math.ceil((diff - today) / DAY_IN_MS);
  return daysAgo;
}

export const LogViewer = React.memo(({ logs: { events } }: LogViewerProps) => {
  const ips = events.reduce(
    (a, n) => { a[n.id] = (a[n.id] ?? n.ip); return a; },
    {} as { [key: string]: string }
  );

  const otherLocations = events.filter(e => e.event[0] === 'LOCATION').map(e => e.event[1] ?? {});
  const location = (id: string) => locations.concat(otherLocations).find(l => l.ip === ips[id]);

  return (
    <Dialog.Root open={true}>
      <Dialog.Content>
        <Dialog.Title>Logs</Dialog.Title>
        <Card my="2">
          <Grid columns="auto minmax(0, 1fr)" gapY="1" gapX="3">
            {events.slice(0).reverse().filter(l => location(l.id)).map((log, index) => {
              const daysAgo = getDaysAgo(log.time);

              const eventData = (log.event[0] === 'USER_INFO') ? (
                <Grid columns="auto minmax(0, 1fr)" gapY="1" gapX="3">
                  <Text size="1" weight='medium' align='right'>Browser</Text>
                  <Text size="1">{log.event[1].browser}</Text>
                  <Text size="1" weight='medium' align='right'>Language</Text>
                  <Text size="1">{log.event[1].language}</Text>
                </Grid>
              ) : <Text size="1">{log.event.map(a => JSON.stringify(a)).join(', ')}</Text>;

              return (
                <React.Fragment key={`${log.id}-${log.time}-${index}`}>
                  <Flex direction='column' className="alternate-bg">
                    {daysAgo && <Text size="1">{daysAgo} day{daysAgo > 1 ? 's' : ''} ago</Text>}
                    <Text size="1">{new Date(Date.parse(log.time)).toLocaleTimeString()}</Text>
                    <Text size="1">{location(log.id)?.city}, {location(log.id)?.region_code}</Text>
                  </Flex>
                  <Text className="alternate-bg">{eventData}</Text>
                </React.Fragment>
              );
            })}
          </Grid>
        </Card>
      </Dialog.Content>
    </Dialog.Root>
  );
})
